import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { I18nProvider, useI18n } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import {
  StorefrontProvider,
  storefrontOrderingPaused,
  useStorefront,
} from "@/lib/storefront";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { LoginDialog } from "@/components/LoginDialog";
import { Toaster } from "@/components/ui/sonner";
import { MobileCartBar } from "@/components/cart/MobileCartBar";
import {
  CustomerErrorViewerProvider,
  useCustomerErrorViewer,
} from "@/components/customer-error-viewer";
import { useStorefrontTracking } from "@/lib/tracking";
import { SeoController } from "@/components/SeoController";
import {
  installBrowserErrorTelemetry,
  reportBrowserError,
} from "@/lib/browser-error-telemetry";

function NotFoundComponent() {
  const { t } = useI18n();
  return (
    <div className="pattern-levant flex min-h-[70vh] items-center justify-center px-4">
      <div className="surface-card max-w-md px-8 py-12 text-center">
        <p className="text-7xl font-black text-primary">404</p>
        <h1 className="mt-4 text-xl font-bold text-foreground">
          {t("notfound.title")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("notfound.desc")}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition hover:brightness-110"
          >
            {t("notfound.home")}
          </Link>
          <Link
            to="/menu"
            className="inline-flex items-center justify-center rounded-full border border-border bg-card px-5 py-2.5 text-sm font-bold transition hover:bg-accent"
          >
            {t("notfound.menu")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => reportBrowserError(error, "boundary"), [error]);
  const lang = preferredLanguage();
  const copy =
    lang === "ar"
      ? {
          title: "تعذّر تحميل الصفحة",
          description:
            "حدث خطأ غير متوقع. حاول مرة أخرى أو ارجع إلى الصفحة الرئيسية.",
          retry: "إعادة المحاولة",
          home: "العودة للرئيسية",
        }
      : {
          title: "This page didn't load",
          description: "Something went wrong. Try again or head back home.",
          retry: "Try again",
          home: "Go home",
        };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {copy.title}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{copy.description}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {copy.retry}
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {copy.home}
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    component: RootComponent,
    notFoundComponent: NotFoundComponent,
    errorComponent: ErrorComponent,
  },
);

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(installBrowserErrorTelemetry, []);

  return (
    <QueryClientProvider client={queryClient}>
      <StorefrontProvider>
        <I18nProvider>
          <CustomerErrorViewerProvider>
            <AuthProvider>
              <CartProvider>
                <AppChrome />
              </CartProvider>
            </AuthProvider>
          </CustomerErrorViewerProvider>
        </I18nProvider>
      </StorefrontProvider>
    </QueryClientProvider>
  );
}

function AppChrome() {
  const { t } = useI18n();
  const storefront = useStorefront();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const isPaymentLinkPage = pathname.startsWith("/pay/");
  useStorefrontTracking(storefront, pathname);
  const seo = <SeoController pathname={pathname} />;
  if (isPaymentLinkPage) {
    return (
      <>
        {seo}
        <Outlet />
        <Toaster position="top-center" richColors closeButton />
      </>
    );
  }
  return (
    <>
      {seo}
      <OrderingPauseDialog />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:start-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        {t("nav.skip")}
      </a>
      <div className="flex min-h-screen flex-col">
        <Header />
        <main
          id="main-content"
          className={
            pathname === "/"
              ? "flex flex-1 flex-col [&>section]:flex-1"
              : "flex-1"
          }
        >
          <Outlet />
        </main>
        <Footer flush={pathname === "/"} />
      </div>
      <MobileCartBar />
      <LoginDialog />
      <Toaster position="top-center" richColors closeButton />
    </>
  );
}

function OrderingPauseDialog() {
  const storefront = useStorefront();
  const { showError } = useCustomerErrorViewer();
  const paused = storefrontOrderingPaused(storefront);

  useEffect(() => {
    if (paused) showError({ code: "ordering_paused" });
  }, [paused, showError]);

  return null;
}

function preferredLanguage(): "ar" | "en" {
  return "ar";
}
